import { useState, useCallback, useEffect } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowRight, Loader2 } from "lucide-react";
import type { FeedbackStatus } from "@/shared/schemas/feedback.schema";
import {
  FEEDBACK_MAX_IMAGES,
  FEEDBACK_RESOLUTION_NOTE_MAX,
  FEEDBACK_TYPES_REQUIRE_AFTER_IMAGE,
  findFeedbackTransition,
} from "@/shared/schemas/feedbackStatus";
import {
  FEEDBACK_SHEET_BODY_CLASS,
  FEEDBACK_SHEET_CONTENT_CLASS,
  FEEDBACK_SHEET_FOOTER_CLASS,
  FEEDBACK_SHEET_HEADER_CLASS,
  FeedbackImagePickerButtons,
  FeedbackStatusChip,
  FeedbackThumbGrid,
  uploadFeedbackImages,
} from "./feedback-ui";

export interface FeedbackStateTransitionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  oldStatus: FeedbackStatus | null;
  newStatus: FeedbackStatus | null;
  /** Loại phiếu — Lỗi bắt buộc ảnh sau khi sửa khi Hoàn thành */
  feedbackType?: string | null;
  onConfirm: (data: { note: string; images: string[] }) => void | Promise<void>;
  isSubmitting?: boolean;
}

/**
 * Dialog chuyển trạng thái (chuẩn EMS 4.4): mobile là bottom sheet.
 * - Hoàn thành: "Kết quả xử lý" (0/400) + ảnh sau khi sửa (bắt buộc với Lỗi).
 * - Mở lại: lý do bắt buộc. Đang xử lý / Đóng: ghi chú tùy chọn.
 * Server (feedback.updateStatus) kiểm tra lại toàn bộ điều kiện này.
 */
export function FeedbackStateTransitionDialog({
  open,
  onOpenChange,
  oldStatus,
  newStatus,
  feedbackType,
  onConfirm,
  isSubmitting = false,
}: FeedbackStateTransitionDialogProps) {
  const [note, setNote] = useState("");
  const [images, setImages] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (open) {
      setNote("");
      setImages([]);
      setIsUploading(false);
    }
  }, [open]);

  const rule = findFeedbackTransition(oldStatus, newStatus);
  const isResolve = newStatus === "RESOLVED";
  const isReopen = rule?.kind === "reopen";
  const requiresImage = isResolve && FEEDBACK_TYPES_REQUIRE_AFTER_IMAGE.includes(feedbackType || "");
  const requiresNote = Boolean(rule?.requireNote);
  const noteTooLong = isResolve && note.trim().length > FEEDBACK_RESOLUTION_NOTE_MAX;

  const addFiles = useCallback(
    async (files: File[]) => {
      setIsUploading(true);
      try {
        const urls = await uploadFeedbackImages(files, FEEDBACK_MAX_IMAGES - images.length, "Ảnh sau khi sửa");
        if (urls.length) setImages((prev) => [...prev, ...urls].slice(0, FEEDBACK_MAX_IMAGES));
      } finally {
        setIsUploading(false);
      }
    },
    [images.length],
  );

  const handlePaste = useCallback(
    async (e: React.ClipboardEvent) => {
      const files = Array.from(e.clipboardData?.items || [])
        .filter((it) => it.type.startsWith("image/"))
        .map((it) => it.getAsFile())
        .filter((f): f is File => !!f);
      if (files.length) {
        e.preventDefault();
        await addFiles(files);
      }
    },
    [addFiles],
  );

  const missingReason = (() => {
    if (requiresNote && !note.trim()) return isResolve ? "Vui lòng nhập kết quả xử lý" : "Vui lòng nhập lý do mở lại";
    if (noteTooLong) return `Kết quả xử lý tối đa ${FEEDBACK_RESOLUTION_NOTE_MAX} ký tự`;
    if (requiresImage && images.length === 0) return "Phiếu loại Lỗi cần ít nhất 1 ảnh sau khi sửa";
    return null;
  })();

  const handleConfirm = async () => {
    if (missingReason) {
      toast.error(missingReason);
      return;
    }
    try {
      await onConfirm({ note: note.trim(), images });
      onOpenChange(false);
    } catch {
      // Lỗi đã được thông báo ở nơi gọi; giữ dialog mở để sửa lại
    }
  };

  const noteLabel = isResolve ? "Kết quả xử lý" : isReopen ? "Lý do mở lại" : "Ghi chú";
  const notePlaceholder = isResolve
    ? "VD: Đã sửa lỗi không lưu được đơn hàng. Anh/chị vui lòng thử lại."
    : isReopen
      ? "Nhập lý do cần xử lý lại..."
      : "Ghi chú thêm (không bắt buộc)";
  const title = rule?.actionLabel || "Chuyển trạng thái";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={FEEDBACK_SHEET_CONTENT_CLASS}
        onPaste={handlePaste}
        data-testid="fb-transition-dialog"
      >
        <DialogHeader className={FEEDBACK_SHEET_HEADER_CLASS}>
          <DialogTitle className="text-base font-bold text-slate-800 pr-8">{title}</DialogTitle>
          <DialogDescription asChild>
            <div className="flex flex-wrap items-center gap-2 pt-1 text-sm">
              {oldStatus && <FeedbackStatusChip status={oldStatus} />}
              <ArrowRight className="h-4 w-4 text-slate-400" />
              {newStatus && <FeedbackStatusChip status={newStatus} />}
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className={FEEDBACK_SHEET_BODY_CLASS}>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="fb-transition-note" className="text-sm font-semibold text-slate-700">
                {noteLabel} {requiresNote && <span className="text-red-500">*</span>}
              </Label>
              {isResolve && (
                <span
                  data-testid="fb-note-counter"
                  className={cn_counter(noteTooLong)}
                >
                  {note.trim().length}/{FEEDBACK_RESOLUTION_NOTE_MAX}
                </span>
              )}
            </div>
            <Textarea
              id="fb-transition-note"
              data-testid="fb-transition-note"
              placeholder={notePlaceholder}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="min-h-[96px] text-sm resize-none"
            />
            {isResolve && (
              <p className="text-xs text-slate-500">
                Viết ngắn gọn bằng ngôn ngữ người dùng, không ghi chi tiết kỹ thuật.
              </p>
            )}
          </div>

          {isResolve && (
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-slate-700">
                Ảnh sau khi sửa ({images.length}/{FEEDBACK_MAX_IMAGES}){" "}
                {requiresImage && <span className="text-red-500">*</span>}
              </Label>
              <FeedbackImagePickerButtons
                testIdPrefix="fb-after"
                onFiles={addFiles}
                isUploading={isUploading}
                disabled={isUploading || images.length >= FEEDBACK_MAX_IMAGES}
                desktopLabel="Chọn ảnh sau khi sửa"
              />
              <FeedbackThumbGrid
                testId="fb-after-thumbs"
                images={images}
                isUploading={isUploading}
                onRemove={(idx) => setImages((prev) => prev.filter((_, i) => i !== idx))}
              />
              <p className="hidden sm:block text-xs text-slate-400">Có thể dán ảnh bằng Ctrl+V.</p>
            </div>
          )}

          {missingReason && (note.length > 0 || images.length > 0) && (
            <p className="text-sm text-red-600" data-testid="fb-transition-error">{missingReason}</p>
          )}
        </div>

        <div className={FEEDBACK_SHEET_FOOTER_CLASS}>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting || isUploading}
            className="min-h-11 sm:min-h-9 flex-1 sm:flex-none"
          >
            Hủy
          </Button>
          <Button
            type="button"
            data-testid="fb-transition-confirm"
            onClick={handleConfirm}
            disabled={Boolean(missingReason) || isSubmitting || isUploading}
            className="min-h-11 sm:min-h-9 flex-1 sm:flex-none bg-blue-600 hover:bg-blue-700 text-white gap-1.5"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Đang lưu...
              </>
            ) : (
              "Xác nhận"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function cn_counter(over: boolean) {
  return over ? "text-xs font-semibold text-red-600" : "text-xs text-slate-400";
}
